'use client';

import { c as _c } from "react/compiler-runtime";
import { Checkbox, Divider, Stack, Typography } from '@mui/material';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';

/** One selectable row, at either level of the tree. */
interface CheckboxTreeItem {
  id: string;
  label: string;
  selected: boolean;
  /** Sits between the checkbox and the label — a severity dot, say. */
  adornment?: React.ReactNode;
}
export interface CheckboxTreeGroup extends CheckboxTreeItem {
  children: CheckboxTreeItem[];
  /** Sits at the end of the row — a help icon, say. Revealed on row hover. */
  trailing?: React.ReactNode;
}
const ROW_GAP = '12px';
const ROW_HORIZONTAL_PADDING = '0px';
const GROUP_VERTICAL_PADDING = '12px';
const CHILD_VERTICAL_PADDING = '4px';
const CHECKBOX_PADDING = '2px';
const CONTROL_SLOT_WIDTH = '24px';
const CHILD_PADDING_LEFT = `calc(${CONTROL_SLOT_WIDTH} + ${ROW_GAP})`;
const CHECKBOX_ICON_SIZE = '24px';
const CHECKBOX_HEIGHT = `calc(${CHECKBOX_ICON_SIZE} + 2 * ${CHECKBOX_PADDING})`;
const CHILD_LABEL_LINE_HEIGHT = '16px';
// A wrapping label keeps its first line next to the checkbox instead of centring the
// whole block against it, so the row aligns to the top and the first line is padded
// out to share the checkbox's height — as is the adornment beside it.
const WRAPPED_LABEL_TOP_PADDING = `calc((${CHECKBOX_HEIGHT} - ${CHILD_LABEL_LINE_HEIGHT}) / 2)`;
const TreeGroup = t0 => {
  const $ = _c(39);
  const {
    group,
    groupTestIdPrefix,
    childTestIdPrefix,
    wrapChildLabels,
    onGroupToggle,
    onChildToggle
  } = t0;
  let t1;
  if ($[0] !== group.children) {
    t1 = group.children.some(_temp);
    $[0] = group.children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const childSelected = t1;
  const hasChildren = group.children.length > 0;
  const t2 = `${groupTestIdPrefix}${group.id}-expand`;
  const t3 = `material-symbols-arrow-right-rounded${hasChildren ? "" : " invisible"}`;
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = <i data-testid={t2} className={t3} />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      minHeight: "auto"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      gap: ROW_GAP,
      height: "auto",
      minHeight: "auto",
      padding: `${GROUP_VERTICAL_PADDING} ${ROW_HORIZONTAL_PADDING}`,
      flexDirection: "row-reverse",
      "&.Mui-expanded": t5,
      "& .MuiAccordionSummary-content": {
        margin: "0px",
        minWidth: "0px",
        paddingLeft: "0px",
        "& h6": {
          fontWeight: "400 !important"
        }
      },
      "& .MuiAccordionSummary-expandIconWrapper": {
        alignItems: "center",
        height: CONTROL_SLOT_WIDTH,
        justifyContent: "center",
        width: CONTROL_SLOT_WIDTH
      }
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      alignItems: "center",
      gap: ROW_GAP,
      minWidth: "0px",
      width: "100%"
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = `${groupTestIdPrefix}${group.id}`;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      padding: CHECKBOX_PADDING
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const t10 = !group.selected && childSelected;
  let t11;
  if ($[9] !== group.selected || $[10] !== onGroupToggle || $[11] !== t10 || $[12] !== t8) {
    t11 = <Checkbox data-testid={t8} sx={t9} size="small" checked={group.selected} indeterminate={t10} onClick={_temp2} onChange={onGroupToggle} />;
    $[9] = group.selected;
    $[10] = onGroupToggle;
    $[11] = t10;
    $[12] = t8;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== onGroupToggle) {
    t12 = (text, ref) => <Typography ref={ref} variant="subtitle1" className="cursor-pointer truncate" sx={{
      minWidth: "0px"
    }} onClick={e_0 => {
      e_0.stopPropagation();
      onGroupToggle();
    }}>{text}</Typography>;
    $[14] = onGroupToggle;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== group.label || $[17] !== t12) {
    t13 = <OverflowTooltip text={group.label} renderContent={t12} />;
    $[16] = group.label;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== group.trailing) {
    t14 = group.trailing && <Stack onClick={_temp3} sx={{
      marginLeft: "auto",
      justifyContent: "center"
    }}>{group.trailing}</Stack>;
    $[19] = group.trailing;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== group.adornment || $[22] !== t11 || $[23] !== t13 || $[24] !== t14) {
    t15 = <Stack direction="row" className="group/help" sx={t7}>{t11}{group.adornment}{t13}{t14}</Stack>;
    $[21] = group.adornment;
    $[22] = t11;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t15 || $[27] !== t4) {
    t16 = <AccordionSummary expandIcon={t4} sx={t6}>{t15}</AccordionSummary>;
    $[26] = t15;
    $[27] = t4;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== childTestIdPrefix || $[30] !== group.children || $[31] !== group.selected || $[32] !== hasChildren || $[33] !== onChildToggle || $[34] !== wrapChildLabels) {
    t17 = hasChildren && <AccordionDetails sx={{
      padding: "0px"
    }}><Stack sx={{
        gap: "0px"
      }}>{group.children.map(child_0 => <Stack key={child_0.id} direction="row" sx={{
          alignItems: wrapChildLabels ? "flex-start" : "center",
          gap: ROW_GAP,
          padding: `${CHILD_VERTICAL_PADDING} ${ROW_HORIZONTAL_PADDING} ${CHILD_VERTICAL_PADDING} ${CHILD_PADDING_LEFT}`
        }}><Checkbox data-testid={`${childTestIdPrefix}${child_0.id}`} sx={{
            padding: CHECKBOX_PADDING
          }} size="small" checked={group.selected || child_0.selected} onChange={() => onChildToggle(child_0.id)} />{child_0.adornment && <Stack sx={{
            height: CHECKBOX_HEIGHT,
            justifyContent: "center"
          }}>{child_0.adornment}</Stack>}<ChildLabel label={child_0.label} wrap={wrapChildLabels} onClick={() => onChildToggle(child_0.id)} /></Stack>)}</Stack></AccordionDetails>;
    $[29] = childTestIdPrefix;
    $[30] = group.children;
    $[31] = group.selected;
    $[32] = hasChildren;
    $[33] = onChildToggle;
    $[34] = wrapChildLabels;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t16 || $[37] !== t17) {
    t18 = <Accordion>{t16}{t17}</Accordion>;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  return t18;
};
const ChildLabel = t0 => {
  const $ = _c(9);
  const {
    label,
    wrap,
    onClick
  } = t0;
  if (wrap) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        minWidth: "0px",
        paddingTop: WRAPPED_LABEL_TOP_PADDING
      };
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== label || $[2] !== onClick) {
      t2 = <Typography variant="body2" className="cursor-pointer" sx={t1} onClick={onClick}>{label}</Typography>;
      $[1] = label;
      $[2] = onClick;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== onClick) {
    t1 = (text, ref) => <Typography ref={ref} variant="body2" className="cursor-pointer truncate" sx={{
      minWidth: "0px"
    }} onClick={onClick}>{text}</Typography>;
    $[4] = onClick;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== label || $[7] !== t1) {
    t2 = <OverflowTooltip text={label} renderContent={t1} />;
    $[6] = label;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};

/**
 * A two-level checkbox list: groups that can be ticked as a whole, each holding
 * children that can be ticked individually. Selection state is computed by the
 * caller and handed in on the model, so the same tree serves filters whose
 * levels write different properties (data classes and their entity types,
 * control severities and the controls inside them).
 */
export const CheckboxTreeFilter = t0 => {
  const $ = _c(17);
  const {
    groups,
    groupTestIdPrefix,
    childTestIdPrefix,
    wrapChildLabels: t1,
    onGroupToggle,
    onChildToggle
  } = t0;
  const wrapChildLabels = t1 === undefined ? false : t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Divider sx={{
      borderStyle: "dashed"
    }} />;
    t3 = {
      gap: "0px",
      paddingY: GROUP_VERTICAL_PADDING
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] !== childTestIdPrefix || $[3] !== groupTestIdPrefix || $[4] !== groups || $[5] !== onChildToggle || $[6] !== onGroupToggle || $[7] !== wrapChildLabels) {
    let t5;
    if ($[9] !== childTestIdPrefix || $[10] !== groupTestIdPrefix || $[11] !== onChildToggle || $[12] !== onGroupToggle || $[13] !== wrapChildLabels) {
      t5 = group => <TreeGroup key={group.id} group={group} groupTestIdPrefix={groupTestIdPrefix} childTestIdPrefix={childTestIdPrefix} wrapChildLabels={wrapChildLabels} onGroupToggle={() => onGroupToggle(group.id)} onChildToggle={childId => onChildToggle(group.id, childId)} />;
      $[9] = childTestIdPrefix;
      $[10] = groupTestIdPrefix;
      $[11] = onChildToggle;
      $[12] = onGroupToggle;
      $[13] = wrapChildLabels;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t4 = groups.map(t5);
    $[2] = childTestIdPrefix;
    $[3] = groupTestIdPrefix;
    $[4] = groups;
    $[5] = onChildToggle;
    $[6] = onGroupToggle;
    $[7] = wrapChildLabels;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[15] !== t4) {
    t5 = <Stack divider={t2} sx={t3}>{t4}</Stack>;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
};
function _temp(child) {
  return child.selected;
}
function _temp2(e) {
  return e.stopPropagation();
}
function _temp3(e_1) {
  return e_1.stopPropagation();
}